/**
 * Angel of Peace Academy — Planned Course of Study
 *
 * Structured curriculum data used by <CurriculumAccordion />.
 * Where an exact grade placement has NOT been established for the Academy,
 * the resource is either omitted or marked "To be finalized" so that we
 * never invent commitments on behalf of the school.
 *
 * Levels shape:
 *   {
 *     grade:       string   (e.g. "Kindergarten", "Grade 3")
 *     resources:   string[] (curriculum resources / programs planned)
 *     focus:       string[] (major areas of development / principal studies)
 *     description: string   (optional short course description)
 *     books:       Book[]   (optional visible sample books; ≈3-4 items)
 *     moreBooks:   Book[]   (optional additional sample books hidden behind a
 *                            "View Sample Booklist +" nested disclosure)
 *   }
 *
 * Book shape:
 *   { title: string, author?: string }
 */

export const TBD = "To be finalized";

// ---------------------------------------------------------------------------
// 1. Reading, Grammar & Composition
// ---------------------------------------------------------------------------
const readingLevels = [
  {
    grade: "Kindergarten",
    resources: ["Memoria Press — First Start Reading", "Memoria Press — Copybook I"],
    focus: [
      "Phonics",
      "Letter formation & handwriting",
      "Oral narration begins",
      "Memory work: prayers, poems, nursery rhymes",
    ],
  },
  {
    grade: "Grade 1",
    resources: [
      "Memoria Press — First Start Reading (continued)",
      "Memoria Press — Traditional Spelling I",
      "New American Cursive I (or Memoria Press Copybook)",
    ],
    focus: [
      "Reading fluency",
      "Spelling and copywork",
      "Simple oral narration of read-alouds",
      "Continued memory work",
    ],
  },
  {
    grade: "Grade 2",
    resources: [
      "Memoria Press — Traditional Spelling II",
      "Memoria Press — English Grammar Recitation, Book 1",
      "Cursive handwriting",
    ],
    focus: [
      "Fluent independent reading",
      "Parts of speech (introduced through recitation)",
      "Copywork of well-formed sentences",
      "Written narration begins",
    ],
  },
  {
    grade: "Grade 3",
    resources: [
      "Memoria Press — Traditional Spelling III",
      "Memoria Press — English Grammar Recitation, Book 2",
      "Memoria Press — Classical Composition: Fable Stage",
    ],
    focus: [
      "Grammar of the sentence",
      "Studied dictation",
      "Imitation of fables through variation, expansion, and condensation",
    ],
  },
  {
    grade: "Grade 4",
    resources: [
      "Memoria Press — English Grammar Recitation, Book 3",
      "Memoria Press — Classical Composition: Narrative Stage",
    ],
    focus: [
      "Narrative structure",
      "Rewriting historical and literary narratives",
      "Vocabulary and figures of speech",
    ],
  },
  {
    grade: "Grade 5",
    resources: [
      "Memoria Press — English Grammar Recitation, Book 4",
      "Memoria Press — Classical Composition: Chreia & Maxim Stage",
    ],
    focus: [
      "Amplification of a saying or brief anecdote",
      "Formal paragraph development",
      "Continued grammar review",
    ],
  },
  {
    grade: "Grade 6",
    resources: [
      "Memoria Press — English Grammar Recitation, Book 5",
      "Memoria Press — Classical Composition: Refutation & Confirmation Stage",
    ],
    focus: [
      "Argument: refuting or confirming a narrative",
      "Introduction to formal reasoning in writing",
      "Sentence variety and rhetorical figures",
    ],
  },
  {
    grade: "Grade 7",
    resources: [
      "Memoria Press — Classical Composition: Common Topic Stage",
      "Advanced English grammar — " + TBD,
    ],
    focus: [
      "Extended essay on virtue or vice",
      "Marshalling multiple sources into one composition",
      "Precision of diction",
    ],
  },
  {
    grade: "Grade 8",
    resources: [
      "Memoria Press — Classical Composition: Encomium / Invective / Comparison Stages",
    ],
    focus: [
      "Outline and reconstruct narratives",
      "Paraphrase ideas in the student's own words",
      "Vary vocabulary and sentence structure with intention",
      "Expand and condense passages according to purpose",
      "Consider how arrangement and expression affect meaning",
      "Organize thoughts with increasing independence",
      "Produce clear, coherent, polished compositions rooted in what has been read and studied",
    ],
  },
];

// ---------------------------------------------------------------------------
// 2. Arithmetic — Singapore Mathematics
// ---------------------------------------------------------------------------
// The Academy has selected Singapore Mathematics for the Lower School.  Exact
// edition and grade-by-grade product names (Primary Mathematics vs Dimensions
// Math, etc.) will be finalized by the school.
const arithmeticLevels = [
  { grade: "Kindergarten", resources: ["Singapore Mathematics — Kindergarten"], focus: ["Number sense to 20", "Counting, sorting, patterns", "Shapes and simple measurement"] },
  { grade: "Grade 1", resources: ["Singapore Mathematics — Grade 1"], focus: ["Addition & subtraction within 100", "Place value (tens and ones)", "Introduction to length, mass, and time"] },
  { grade: "Grade 2", resources: ["Singapore Mathematics — Grade 2"], focus: ["Multiplication tables of 2, 5, 10", "Regrouping in addition and subtraction", "Money, length, mass, and time"] },
  { grade: "Grade 3", resources: ["Singapore Mathematics — Grade 3"], focus: ["Multiplication and division facts", "Introduction to fractions", "Bar models for problem solving"] },
  { grade: "Grade 4", resources: ["Singapore Mathematics — Grade 4"], focus: ["Long multiplication and division", "Fractions and mixed numbers", "Decimals, angles, symmetry"] },
  { grade: "Grade 5", resources: ["Singapore Mathematics — Grade 5"], focus: ["Fraction operations", "Decimal operations", "Percent, ratio, and volume"] },
  { grade: "Grade 6", resources: ["Singapore Mathematics — Grade 6"], focus: ["Ratio and proportion", "Percent and rate", "Introduction to algebraic reasoning"] },
  { grade: "Grade 7", resources: ["Singapore Mathematics — Secondary sequence (" + TBD + ")"], focus: ["Formal algebraic expressions and equations", "Linear relationships", "Geometry: angles, triangles, congruence"] },
  { grade: "Grade 8", resources: ["Singapore Mathematics — Secondary sequence (" + TBD + ")"], focus: ["Algebra: functions and quadratics", "Pythagorean theorem, similarity, area, volume", "Increasingly abstract mathematical thought"] },
];

// ---------------------------------------------------------------------------
// 3. Latin — Memoria Press Latin sequence
// ---------------------------------------------------------------------------
// K–2 is deliberately oral/story-based. Formal grades of Memoria Press books
// are listed in order; grade placement is noted as "To be finalized" where the
// exact year of introduction has not been established for the Academy.
const latinLevels = [
  {
    grade: "Kindergarten – Grade 2",
    description:
      "Latin develops orally in the youngest years through prayer, chant, sacred music, story, and vocabulary — not through textbook study. Students learn common Latin prayers, feast-day antiphons, and short songs, and begin to absorb the sound and rhythm of the language before formal grammar is introduced.",
    resources: [
      "Prayers, hymns, and antiphons of the Little Office",
      "Latin nursery rhymes, songs, and stories",
      "Oral vocabulary lists tied to daily school life",
    ],
    focus: [
      "Pronunciation and ear",
      "Memorized prayers and responses",
      "Vocabulary of the liturgy and the classroom",
    ],
  },
  {
    grade: "Elementary — introduction",
    resources: ["Memoria Press — Prima Latina"],
    focus: [
      "First Latin vocabulary and simple grammar",
      "Latin prayers, songs, and derivatives",
      "Grade placement: " + TBD,
    ],
  },
  {
    grade: "Elementary — continuation",
    resources: ["Memoria Press — Latina Christiana"],
    focus: [
      "Continued vocabulary and derivatives",
      "First declension nouns and first conjugation verbs",
      "Grade placement: " + TBD,
    ],
  },
  {
    grade: "Middle School — First Form",
    resources: ["Memoria Press — First Form Latin"],
    focus: [
      "All five noun declensions (first & second)",
      "Regular verb conjugations in present, imperfect, future",
      "Translation of simple Latin sentences",
      "Grade placement: " + TBD,
    ],
  },
  {
    grade: "Middle School — Second Form",
    resources: ["Memoria Press — Second Form Latin"],
    focus: [
      "Third, fourth, and fifth declensions",
      "All active voice verb tenses",
      "Increasingly complex translation",
      "Grade placement: " + TBD,
    ],
  },
  {
    grade: "Middle School — Third Form",
    resources: ["Memoria Press — Third Form Latin"],
    focus: [
      "Passive voice, participles, infinitives",
      "Compound sentences and translation of continuous Latin",
      "Grade placement: " + TBD,
    ],
  },
  {
    grade: "Middle School — Fourth Form",
    resources: ["Memoria Press — Fourth Form Latin"],
    focus: [
      "Subjunctive mood and complex constructions",
      "Reading of adapted classical passages",
      "Grade placement: " + TBD,
    ],
  },
];

// ---------------------------------------------------------------------------
// 4. Humanities — Mater Amabilis, taught by community
// ---------------------------------------------------------------------------
// Grade 1 is a single-grade community; Grades 2–3, 4–5, and Middle School
// (6–8) are multi-year communities. Middle School humanities is a three-year
// rotation drawn principally from Mater Amabilis Level 3.
const humanitiesLevels = [
  {
    grade: "Kindergarten · Ages 4 – 6",
    resources: ["Mater Amabilis — Preparatory framework"],
    focus: [
      "Fairy tales, nursery poetry, and memory work",
      "Saints' lives and picture books of the liturgical year",
      "Family, parish, and neighborhood",
      "Nature-based geography and picture study",
    ],
    books: [
      { title: "The Tale of Peter Rabbit", author: "Beatrix Potter" },
      { title: "A Child's Garden of Verses", author: "Robert Louis Stevenson" },
      { title: "Winnie-the-Pooh", author: "A. A. Milne" },
      { title: "Mother Goose Nursery Rhymes", author: "" },
    ],
    moreBooks: [
      { title: "The Blue Fairy Book", author: "Andrew Lang" },
      { title: "Fairy Tales", author: "Hans Christian Andersen" },
      { title: "The Complete Tales of Beatrix Potter", author: "Beatrix Potter" },
    ],
  },
  {
    grade: "Grade 1",
    resources: ["Mater Amabilis — Level 1B framework"],
    focus: [
      "Fables, folk tales, and simple saints' lives",
      "Beginning narration of read-alouds",
      "Nature study, picture study, and copywork",
      "Memory work: poetry, prayers, and Scripture",
    ],
    books: [
      { title: "Aesop's Fables", author: "" },
      { title: "Just So Stories", author: "Rudyard Kipling" },
      { title: "The Story of Ferdinand", author: "Munro Leaf" },
      { title: "Fifty Famous Stories Retold", author: "James Baldwin" },
    ],
    moreBooks: [
      { title: "A Child's First Book of Virtues", author: "William J. Bennett" },
      { title: "The Children's Book of Saints", author: "" },
      { title: "Poetry for Young People (selections)", author: "" },
    ],
  },
  {
    grade: "Grades 2 – 3",
    resources: ["Mater Amabilis — Level 1A framework"],
    focus: [
      "First encounters with the ancient world through story",
      "American and local history through living books",
      "Poetry, hymns, and continued memory work",
      "Nature journaling and picture study",
    ],
    books: [
      { title: "The Children's Homer", author: "Padraic Colum" },
      { title: "D'Aulaires' Book of Greek Myths", author: "Ingri & Edgar D'Aulaire" },
      { title: "The Song of Hiawatha", author: "Henry Wadsworth Longfellow" },
      { title: "Little House in the Big Woods", author: "Laura Ingalls Wilder" },
    ],
    moreBooks: [
      { title: "Famous Men of Greece", author: "John H. Haaren" },
      { title: "Famous Men of Rome", author: "John H. Haaren" },
      { title: "Detectives in Togas", author: "Henry Winterfeld" },
      { title: "The Golden Fleece and the Heroes Who Lived Before Achilles", author: "Padraic Colum" },
    ],
  },
  {
    grade: "Grades 4 – 5",
    resources: ["Mater Amabilis — Level 2 framework"],
    focus: [
      "The medieval Christian world; the age of the saints",
      "Substantial historical fiction and biography",
      "Introduction to Shakespeare through prose retellings",
      "Geography of the historical periods studied",
    ],
    books: [
      { title: "The Door in the Wall", author: "Marguerite de Angeli" },
      { title: "Adam of the Road", author: "Elizabeth Janet Gray" },
      { title: "Tales from Shakespeare", author: "Charles & Mary Lamb" },
      { title: "The Story of King Arthur and His Knights", author: "Howard Pyle" },
    ],
    moreBooks: [
      { title: "Otto of the Silver Hand", author: "Howard Pyle" },
      { title: "Men of Iron", author: "Howard Pyle" },
      { title: "The Trumpeter of Krakow", author: "Eric P. Kelly" },
      { title: "The Merry Adventures of Robin Hood", author: "Howard Pyle" },
    ],
  },
  {
    grade: "Grades 6 – 8 · Middle School",
    resources: ["Adapted Mater Amabilis — Level 3 framework"],
    description:
      "Grades 6, 7, and 8 study humanities together as one Middle School community on a three-year rotation. Students entering Middle School enter whichever year of the cycle is currently underway. Historical, literary, geographical, and cultural content rotates; expectations for reading, discussion, and written work rise as students mature.",
    rotation: [
      {
        year: "Year A · Ancient World & Christendom",
        studies: [
          "Ancient civilizations and the classical world",
          "The rise of the Church and the medieval Christian world",
          "Foundational Catholic literature and biography",
          "Beginning close reading and formal discussion",
        ],
        books: [
          { title: "The Aeneid (selections)", author: "Virgil" },
          { title: "Saint Francis", author: "G. K. Chesterton" },
          { title: "The Trumpeter of Krakow", author: "Eric P. Kelly" },
          { title: "Julius Caesar", author: "William Shakespeare" },
        ],
      },
      {
        year: "Year B · Renaissance to the Age of Revolution",
        studies: [
          "The Renaissance, the Catholic missions, and the early modern world",
          "Age of exploration and the founding of nations",
          "Shakespeare (a full play) and Catholic literary voices",
          "Sustained written narration and essay work",
        ],
        books: [
          { title: "A Man for All Seasons", author: "Robert Bolt" },
          { title: "A Tale of Two Cities", author: "Charles Dickens" },
          { title: "The Merchant of Venice", author: "William Shakespeare" },
          { title: "The Path to Rome", author: "Hilaire Belloc" },
        ],
      },
      {
        year: "Year C · The Modern Age",
        studies: [
          "The nineteenth and twentieth centuries",
          "Modern Catholic literature and thought",
          "Extended reading of Chesterton and Tolkien",
          "Formal literary discussion and analytical writing",
        ],
        books: [
          { title: "The Hobbit", author: "J. R. R. Tolkien" },
          { title: "Orthodoxy (selections)", author: "G. K. Chesterton" },
          { title: "The Man Who Was Thursday", author: "G. K. Chesterton" },
          { title: "Macbeth", author: "William Shakespeare" },
        ],
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// 5. Science — Diocese of Marquette (Lower) → Novare (Middle)
// ---------------------------------------------------------------------------
const scienceLevels = [
  {
    grade: "Kindergarten – Grade 5 · Lower School",
    resources: [
      "Diocese of Marquette — Catholic Elementary Science curriculum",
    ],
  },
  {
    grade: "Grades 6 – 8 · Middle School",
    resources: ["Novare Science: Life Science, Physical Science, and Earth Science"],
  },
];

// ---------------------------------------------------------------------------
// Exported sections
// ---------------------------------------------------------------------------
export const curriculumSections = [
  {
    id: "reading",
    title: "Reading & Grammar",
    resource: [
      "All About Reading Levels Pre-Reading–4",
      "Memoria Press Traditional Spelling I–VI",
      "Memoria Press Introduction to English Grammar",
      "Memoria Press English Grammar Recitation I–V",
    ],
    progressionNote:
      "The Reading & Grammar program is built to form the strongest foundation in the art of grammar, preparing students for success in courses requiring increasingly difficult reading material and writing expectations. Course placement is based on each student's individual performance.",
  },
  {
    id: "writing",
    title: "Writing & Composition",
    resource: [
      "Institute for Excellence in Writing",
      "Memoria Press Classical Composition 1–5",
    ],
    progressionNote:
      "Our Writing & Composition program follows Memoria Press' Classical Composition course sequence, rooted in the ancient progymnasmata tradition. Students progress through a carefully ordered sequence of writing skills, moving from simple forms to increasingly sophisticated composition. Together with Spelling, English Grammar, and Latin, Classical Composition provides students with a robust foundation in the art of clear, disciplined, and eloquent writing.",
  },
  {
    id: "logic-rhetoric",
    title: "Logic & Rhetoric",
    resource: [
      "Memoria Press Traditional Logic I",
      "IEW Introduction to Public Speaking",
    ],
    progressionNote:
      "Through formal logic, students learn to recognize the structure of sound arguments, identify faulty reasoning, and defend their conclusions with precision. Public speaking then puts these habits into practice as students learn to organize their thoughts, address an audience, and communicate with clarity and confidence. Together, these studies teach students to think clearly, speak confidently, and use persuasion in the service of truth.",
  },
  {
    id: "arithmetic",
    title: "Arithmetic",
    resource: "Singapore Dimensions",
    progression: [
      "Number Sense",
      "Arithmetic Fluency",
      "Multiplication & Division",
      "Fractions & Decimals",
      "Ratio & Proportion",
      "Pre-Algebra",
      "Algebra I & Introductory Geometry",
    ],
    progressionNote:
      "Students completing the full sequence are prepared to advance to Geometry and Algebra II in high school.",
  },
  {
    id: "latin",
    title: "Course Progression",
    resource: ["University of Dallas — Latin Through Stories", "Memoria Press Forms I–IV", "Henle I"],
  },
  // Humanities — four Forms (DK/K → 7-8), a concise on-site view
  {
    id: "hum-form-i",
    humanitiesForm: true,
    eyebrow: "Form I",
    title: "Wonder & Delight",
    meta: "Kindergarten",
    subtitle: "Listen · Delight · Retell",
    formDescription:
      "The earliest Humanities experience begins in wonder and delight. Children are surrounded by fairy tales, fables, nursery rhymes, saints, Scripture stories, poetry, nature books, and enduring picture books. Through frequent read-alouds and conversation, students develop attention, memory, imagination, language, and a love of beautiful stories before Humanities becomes a more formal study.",
    sampleBooks: [
      { title: "The Complete Tales of Winnie-the-Pooh", author: "A. A. Milne" },
      { title: "Tales of Beatrix Potter", author: "Beatrix Potter" },
      { title: "The Velveteen Rabbit", author: "Margery Williams" },
      { title: "Aesop's Fables", author: "Aesop" },
      { title: "Mother Goose Tales and Rhymes", author: "" },
      { title: "The Clown of God", author: "Tomie dePaola" },
      { title: "Saint George and the Dragon", author: "Margaret Hodges" },
      { title: "Make Way for Ducklings", author: "Robert McCloskey" },
    ],
    sampleBooksNote:
      "Selections are representative of a much broader read-aloud library.",
  },
  {
    id: "hum-form-ii",
    humanitiesForm: true,
    eyebrow: "Form II",
    title: "Stories & Discovery",
    meta: "Grades 1–3",
    subtitle: "Hear · Imagine · Narrate",
    formDescription:
      "As children enter the Lower School, stories begin to open the wider world. Across a three-year rotation, students encounter the ancient and legendary world, medieval and Renaissance Christendom, and the American story through history, biography, geography, fairy tale, myth, and classic children's literature.",
    cycleLabel: "Three-Year Rotation",
    rotationCycles: [
      {
        year: "Year A",
        cycle: "Ancient & Legendary World",
        books: [
          { title: "A Child's History of the World", author: "Virgil Hillyer" },
          { title: "The Blue Fairy Book", author: "Andrew Lang" },
          { title: "The Aesop for Children", author: "" },
          { title: "Fifty Famous Stories Retold", author: "James Baldwin" },
          { title: "Parables from Nature", author: "Margaret Gatty" },
          { title: "The Lion, the Witch and the Wardrobe", author: "C. S. Lewis" },
          { title: "Charlotte's Web", author: "E. B. White" },
          { title: "The Wind in the Willows", author: "Kenneth Grahame" },
        ],
      },
      {
        year: "Year B",
        cycle: "Medieval & Renaissance World",
        books: [
          { title: "The Little Duke", author: "Charlotte M. Yonge" },
          { title: "Saint Joan: The Girl Soldier", author: "Louis de Wohl" },
          { title: "Robin Hood", author: "Howard Pyle" },
          { title: "Marco Polo: His Travels and Adventures", author: "George Makepeace Towle" },
          { title: "Leonardo da Vinci", author: "Emily Hahn" },
          { title: "Michelangelo", author: "Diane Stanley" },
          { title: "Francis: The Poor Man of Assisi", author: "Tomie dePaola" },
          { title: "Heidi", author: "Johanna Spyri" },
        ],
      },
      {
        year: "Year C",
        cycle: "America & the Modern World",
        books: [
          { title: "George Washington", author: "Ingri & Edgar Parin d'Aulaire" },
          { title: "Abraham Lincoln", author: "Ingri & Edgar Parin d'Aulaire" },
          { title: "Understood Betsy", author: "Dorothy Canfield Fisher" },
          { title: "Selections from the Little House series", author: "Laura Ingalls Wilder" },
          { title: "American Tall Tales", author: "Mary Pope Osborne" },
          { title: "All-of-a-Kind Family", author: "Sydney Taylor" },
          { title: "Paddle-to-the-Sea", author: "Holling C. Holling" },
          { title: "The Weight of a Mass", author: "Josephine Nobisso" },
        ],
      },
    ],
  },
  {
    id: "hum-form-iii",
    humanitiesForm: true,
    eyebrow: "Form III",
    title: "History & Adventure",
    meta: "Grades 4–6",
    subtitle: "Narrate · Connect · Explore",
    formDescription:
      "In Form III, the sweep of civilization returns at greater depth. Students travel through the ancient classical world, medieval Christendom and the age of exploration, and the American and modern world through increasingly substantial works of history, biography, geography, literature, and adventure.",
    cycleLabel: "Three-Year Rotation",
    rotationCycles: [
      {
        year: "Year A",
        cycle: "Ancient & Classical World",
        books: [
          { title: "The Story of the Greeks", author: "H. A. Guerber" },
          { title: "Augustus Caesar's World", author: "Genevieve Foster" },
          { title: "D'Aulaires' Book of Greek Myths", author: "Ingri & Edgar Parin d'Aulaire" },
          { title: "The Heroes of Asgard", author: "Annie & Eliza Keary" },
          { title: "Plutarch's Lives (selections)", author: "" },
          { title: "Tales from Shakespeare", author: "Charles & Mary Lamb" },
          { title: "Gregor Mendel: The Friar Who Grew Peas", author: "Cheryl Bardoe" },
          { title: "The Chronicles of Narnia", author: "C. S. Lewis" },
        ],
      },
      {
        year: "Year B",
        cycle: "Medieval Christendom & Exploration",
        books: [
          { title: "The Door in the Wall", author: "Marguerite de Angeli" },
          { title: "Adam of the Road", author: "Elizabeth Janet Gray" },
          { title: "Otto of the Silver Hand", author: "Howard Pyle" },
          { title: "The Trumpeter of Krakow", author: "Eric P. Kelly" },
          { title: "King Arthur and His Knights of the Round Table", author: "Roger Lancelyn Green" },
          { title: "Captains Courageous", author: "Rudyard Kipling" },
          { title: "Treasure Island", author: "Robert Louis Stevenson" },
          { title: "Kon-Tiki", author: "Thor Heyerdahl" },
        ],
      },
      {
        year: "Year C",
        cycle: "America & the Modern World",
        books: [
          { title: "George Washington's World", author: "Genevieve Foster" },
          { title: "Poor Richard", author: "James Daugherty" },
          { title: "Johnny Tremain", author: "Esther Forbes" },
          { title: "The Sign of the Beaver", author: "Elizabeth George Speare" },
          { title: "Amos Fortune, Free Man", author: "Elizabeth Yates" },
          { title: "The Adventures of Tom Sawyer", author: "Mark Twain" },
          { title: "Little Women", author: "Louisa May Alcott" },
          { title: "My Side of the Mountain", author: "Jean Craighead George" },
        ],
      },
    ],
  },
  {
    id: "hum-form-iv",
    humanitiesForm: true,
    eyebrow: "Form IV",
    title: "Ideas & Civilization",
    meta: "Grades 7–8",
    subtitle: "Encounter · Compare · Discern",
    formDescription:
      "In Form IV, students enter more deeply into the great conversation of civilization. The first year moves from the classical world into medieval Christendom; the second follows the Renaissance, Reformation, revolution, and modern world. Students encounter increasingly substantial works of literature, history, philosophy, and the Catholic intellectual tradition while learning to compare ideas and discern what is true, good, and beautiful.",
    cycleLabel: "Two-Year Rotation",
    rotationCycles: [
      {
        year: "Year A",
        cycle: "Classical & Medieval World",
        books: [
          { title: "The Odyssey", author: "Homer" },
          { title: "The Aeneid", author: "Virgil" },
          { title: "The Confessions", author: "St. Augustine" },
          { title: "Beowulf", author: "trans. Burton Raffel" },
          { title: "Sir Gawain and the Green Knight", author: "" },
          { title: "The Divine Comedy — Selected Cantos", author: "Dante Alighieri" },
          { title: "The Ballad of the White Horse", author: "G. K. Chesterton" },
          { title: "The Hobbit", author: "J. R. R. Tolkien" },
        ],
      },
      {
        year: "Year B",
        cycle: "Renaissance, Counter-Reformation & Modern World",
        books: [
          { title: "Utopia", author: "St. Thomas More" },
          { title: "I Promessi Sposi (The Betrothed)", author: "Alessandro Manzoni" },
          { title: "Come Rack! Come Rope!", author: "Robert Hugh Benson" },
          { title: "The Song at the Scaffold", author: "Gertrud von Le Fort" },
          { title: "Macbeth", author: "William Shakespeare" },
          { title: "Julius Caesar", author: "William Shakespeare" },
          { title: "A Tale of Two Cities", author: "Charles Dickens" },
          { title: "The Screwtape Letters", author: "C. S. Lewis" },
        ],
      },
    ],
  },
  {
    id: "science",
    title: "Course of Study",
    levels: scienceLevels,
  },
];
